import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Home, Building2, ArrowRight, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceTypes = [
  { id: 'domiciliary', label: 'Domiciliary Care', description: 'Home-based care services' },
  { id: 'supported_living', label: 'Supported Living', description: 'Independent living support' },
  { id: 'care_home', label: 'Care Home', description: 'Residential care facilities' },
];

const moduleOptions = [
  { id: 'dashboard', label: 'Dashboard', included: true },
  { id: 'staff', label: 'Staff Management', included: true },
  { id: 'clients', label: 'Client Management', included: true },
  { id: 'compliance', label: 'CQC Compliance', included: true },
  { id: 'care_plans', label: 'Care Plans', included: false },
  { id: 'daily_logs', label: 'Daily Logs', included: false },
  { id: 'incidents', label: 'Incident Reporting', included: false },
  { id: 'medications', label: 'MAR Charts', included: false },
  { id: 'documents', label: 'Documents', included: false },
  { id: 'training', label: 'Training', included: false },
  { id: 'finance', label: 'Finance & Payroll', included: false },
];

export default function SetupOrganization() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    cqc_registration_number: '',
    address: '',
    phone: '',
    email: '',
    service_types: [],
    modules_enabled: ['dashboard', 'staff', 'clients', 'compliance'],
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { updateOrganization } = useAuth();
  const navigate = useNavigate();

  const handleServiceTypeToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(serviceId)
        ? prev.service_types.filter(id => id !== serviceId)
        : [...prev.service_types, serviceId]
    }));
  };

  const handleModuleToggle = (moduleId) => {
    const module = moduleOptions.find(m => m.id === moduleId);
    if (module?.included) return; // Core modules can't be toggled
    
    setFormData(prev => ({
      ...prev,
      modules_enabled: prev.modules_enabled.includes(moduleId)
        ? prev.modules_enabled.filter(id => id !== moduleId)
        : [...prev.modules_enabled, moduleId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.service_types.length === 0) {
      setError('Please select at least one service type');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/organizations`, formData);
      updateOrganization(response.data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create organization');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="setup-organization-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Set Up Your Organization</h1>
          <p className="text-slate-600 mt-2">Let's configure D3 Care Solutions for your needs</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-teal-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Organization Details'}
              {step === 2 && 'Service Types'}
              {step === 3 && 'Select Modules'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Enter your organization information'}
              {step === 2 && 'What types of care services do you provide?'}
              {step === 3 && 'Choose the modules you need (you can add more later)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="setup-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {/* Step 1: Organization Details */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., ABC Care Services Ltd"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="org-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cqc_registration_number">CQC Registration Number</Label>
                    <Input
                      id="cqc_registration_number"
                      placeholder="e.g., 1-XXXXXXXXX"
                      value={formData.cqc_registration_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, cqc_registration_number: e.target.value }))}
                      data-testid="org-cqc"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      placeholder="Full address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      data-testid="org-address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+44 XXXX XXXXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        data-testid="org-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="contact@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="org-email"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Service Types */}
              {step === 2 && (
                <div className="space-y-4">
                  {serviceTypes.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => handleServiceTypeToggle(service.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.service_types.includes(service.id)
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={formData.service_types.includes(service.id)}
                          className="pointer-events-none"
                        />
                        <div>
                          <p className="font-medium text-slate-900">{service.label}</p>
                          <p className="text-sm text-slate-500">{service.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Step 3: Modules */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {moduleOptions.map((module) => (
                      <div
                        key={module.id}
                        onClick={() => handleModuleToggle(module.id)}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          module.included 
                            ? 'border-teal-600 bg-teal-50 cursor-not-allowed'
                            : formData.modules_enabled.includes(module.id)
                              ? 'border-teal-600 bg-teal-50 cursor-pointer'
                              : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                        }`}
                        data-testid={`module-${module.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={formData.modules_enabled.includes(module.id)}
                            disabled={module.included}
                            className="pointer-events-none"
                          />
                          <span className="text-sm font-medium text-slate-900">{module.label}</span>
                          {module.included && (
                            <span className="text-xs text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">Core</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    data-testid="setup-back"
                  >
                    Back
                  </Button>
                )}
                
                {step < 3 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (step === 1 && !formData.name) {
                        setError('Organization name is required');
                        return;
                      }
                      setError('');
                      setStep(step + 1);
                    }}
                    className="ml-auto bg-teal-600 hover:bg-teal-700"
                    data-testid="setup-next"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="ml-auto bg-teal-600 hover:bg-teal-700"
                    disabled={loading}
                    data-testid="setup-submit"
                  >
                    {loading ? 'Creating...' : 'Complete Setup'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
